import React, { ChangeEvent, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Select, Option, Checkbox } from 'neon-design-system';
import { useQuery } from '@apollo/react-hooks';
import { makeStyles, createStyles } from '@material-ui/core';
import { useFormControl, useFormGroup, useManageTemplateForm } from '../../../utils/form.utils';
import { requiredValidator, emailValidator } from '../../../utils/validator.utils';
import ManageTemplate, { ManageTemplateInstance } from '../../ui/ManageTemplate';
import ManageTemplateFormItem from '../../ui/ManageTemplateFormItem';
import FormTextField from '../../ui/form/FormTextField';
import { Desk, User } from '../../../shared/types';
import { ADD_DESK, DELETE_DESK, DESK, EDIT_DESK } from '../../../gql/desks';
import { PAGINATED_USERS_SEARCH } from '../../../gql/users';
import { sortArray } from '../../../utils/sorting.utils';
import { getContributorRoleUsers } from '../../../utils/filtering.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { debounce } from 'lodash';

const returnListUrl = baseUrls.publisher.desks.list;

interface RequestVariables {
	name: string;
	contributors: string;
	description: string;
	version: number;
}

const useStyles = makeStyles(theme => createStyles({
	container: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(6)
	},
	main: {
		flex: 1,
		flexDirection: 'column',
		maxWidth: theme.spacing(150)
	}
}));

export const DeskAddEdit = (): JSX.Element => {
	const { _id } = useParams();
	const classes = useStyles();

	const {refetch: fetchUsers} = useQuery(PAGINATED_USERS_SEARCH, {
		skip: true
	});

	const { data: desk, loading: loadingDesk } = useQuery<{ document: Desk }>(DESK, {
		skip: !_id,
		fetchPolicy: 'network-only',
		variables: { id: _id },
		onCompleted: ({ document }) => {
			const contributors: Option[] = document.contributors.map(c => ({ label: `${c.name} (${c.email})`, value: c._id }))
			form.name.setInitialValue(document.name);
			form.contributors.setInitialValue(contributors);
			form.email.setInitialValue(document.email);
		}
	});

	const form = useFormGroup({
		name: useFormControl<string>('', [requiredValidator]),
		contributors: useFormControl<Option[] | undefined>([], [requiredValidator]),
		email: useFormControl<string>('', [emailValidator]),
		allUsers: useFormControl<boolean>(false, [])
	});

	const findUsers = useCallback(debounce((input: string, callback) => {
		fetchUsers({
			params: {
				searchQuery: input
			}
		}).then(r => {
			const users: User[] = r.data.getPaginatedUsers.users;
			const filteredUsers = form.allUsers.value ? users : getContributorRoleUsers(users);

			const usersSelectOptions: Option[] = sortArray(
				filteredUsers.map((user): Option => ({
					label: `${user.name} (${user.email})`,
					value: user._id
				})), 'label');
			callback(usersSelectOptions);
		});
	}, 500), [desk, form.allUsers]);

	const editItem = desk?.document;

	const { isNew, formMessage, formBusy, onSubmit, onCancel, onDelete, onAlertBoxClose } = useManageTemplateForm(
		ManageTemplateInstance.Desk,
		form,
		editItem,
		ADD_DESK,
		EDIT_DESK,
		DELETE_DESK,
		returnListUrl
	);

	if (loadingDesk) {
		return <Loader/>;
	}

	const getReqBody = (): RequestVariables => {
		return {
			...form.value,
			contributors: (form.contributors.value || []).map(({ value }: Option) => value),
			version: editItem?.version || undefined,
			description: editItem?.description || form.name.value,
			allUsers: undefined
		}
	};

	const selectedContributors = form?.contributors?.value ?? [];
	const channels = desk?.document.channels || [];
	const showDelete = !channels.length;

	return (
		<div className={classes.container}>
			<div className={classes.main}>
				<ManageTemplate
					dialogMessage={<>You are about to delete the desk
						<strong> {form?.name?.value}</strong>.
						This action cannot be undone, so you must be sure and confirm.</>}
					showDelete={showDelete}
					onDelete={(): void => {
						onDelete(form?.name?.value);
					}}
					pathArea={'Desks'}
					returnUrl={returnListUrl}
					templateInstance={ManageTemplateInstance.Desk}
					isNew={isNew}
					formMessage={formMessage}
					formBusy={formBusy}
					onCancel={onCancel}
					onSubmit={(ev: any): void => {
						onSubmit(ev, getReqBody());
					}}
					onAlertBoxClose={onAlertBoxClose}
				>
					<FormTextField
						id="desk-name-input"
						label="Name"
						field={form.name}
						charsLimit={50}
					/>
					<ManageTemplateFormItem>
						<Checkbox
							id="desk-all-users-checkbox"
							label="Show all users"
							checked={form.allUsers?.value}
							value="a"
							onChange={(e: ChangeEvent<any>): void => {
								form.allUsers.setValue(e?.target?.checked);
							}}
						/>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<Select
							id={'desk-contributors-select'}
							size="medium"
							clearable={true}
							multi={true}
							loadOptions={findUsers}
							menuPlacement="bottom"
							placeholder="Start typing…"
							label="Contributors"
							value={selectedContributors.filter(c => c.label).sort((a, b) =>
								a.label.toLowerCase().localeCompare(b.label.toLowerCase())
							)}
							error={form?.contributors?.showError}
							errorText={form?.contributors?.validationMessage}
							onBlur={form?.contributors?.handleBlur}
							onChange={(options: Option[] = []): void => {
								form?.contributors?.setValue(options);
							}}
						/>
					</ManageTemplateFormItem>
					<FormTextField
						id="desk-email-input"
						label="Contact email"
						field={form.email}
						charsLimit={50}
					/>
				</ManageTemplate>
			</div>
		</div>
	);
};

export default DeskAddEdit;
